import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {DetectMethodChanges, getStrikeStep, isValidNumber, isVoid} from '../../utils';
import {MessageBusService} from '../../message-bus.service';
import {AtmStrikeService} from '../../common-services/atm-strike-service/atm-strike.service';
import {AtmStrikeChangedEvent} from '../../common-services/atm-strike-service/atm-strike-changed-event';
import {CashFlowAdjustment} from '../model/CashFlowAdjustment';
import {AdjustmentPricingGridComponent} from '../adjustment-pricing-grid.component';
import {ZonesGridRow, ZonesGridColumn} from '../zones-grid/zones-grid.component';
import {AdjustmentsGroupForZonesGrid} from '../model/AdjustmentsGroup';
import {UserSettingsService} from '../../user-settings.service';
import {SessionService} from '../../authentication/session-service.service';
import {ToastrService} from 'ngx-toastr';
import * as Enumerable from 'linq';

interface PricingStepDisplayRow {
    strike: number;
    column: ZonesGridColumn;
    roles: string[];
    offsetLabel: string;
}

export interface ApgPricingStepsConfig {
    logicalId: string;
    adjustmentType: string;
    expiration: string;
    expirationFull: string;
    underlying: string;
    stepSize: number;
    numSteps: number;
}

@Component({
    selector: 'ets-apg-pricing-steps-popup',
    templateUrl: './apg-pricing-steps-popup.component.html',
    styleUrls: ['./apg-pricing-steps-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgPricingStepsPopupComponent implements OnInit, OnDestroy {

    @Input() root: AdjustmentPricingGridComponent;

    isVisible = false;
    isLoading = false;
    rows: PricingStepDisplayRow[] = [];
    config: ApgPricingStepsConfig;
    settings: { numOfSteps?: number, stepSize?: number } = {};

    private _refreshInterval: any;
    private _stickyAtmSub: Subscription;

    get isSuperUser(): boolean {
        return this._sessionService.isSuperUser;
    }

    readonly allowedStepSizes = [5, 10];

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _userSettings: UserSettingsService,
        private _sessionService: SessionService,
        private _toastr: ToastrService,
        private _messageBus: MessageBusService,
        private _atmStrikeService: AtmStrikeService,
    ) {
    }

    ngOnInit(): void {
        this._refreshInterval = setInterval(() => {
            if (this.isVisible && this.rows.length > 0) {
                this._changeDetector.detectChanges();
            }
        }, 1000);
    }

    ngOnDestroy(): void {
        if (this._refreshInterval) {
            clearInterval(this._refreshInterval);
        }
        this.unsubscribeStickyAtm();
    }

    @DetectMethodChanges()
    show(config: ApgPricingStepsConfig): void {
        this.config = config;
        this.settings = {
            numOfSteps: config.numSteps,
            stepSize: config.stepSize,
        };
        this.isVisible = true;

        this.root?.resetPricingStepsSubscriptions();
        this.isLoading = true;
        this.subscribeStickyAtm(config);
    }

    @DetectMethodChanges()
    setData(adjustments: CashFlowAdjustment[]): void {
        this.isLoading = false;

        if (isVoid(adjustments) || adjustments.length === 0) {
            return;
        }

        const couples = adjustments.filter(x => !isVoid(x.coupleTag));
        const zonesGridRows = couples.length > 0
            ? this.prepareDataCouples(couples)
            : this.prepareDataSingle(adjustments);

        const expiration = this.config?.expiration;

        // Ground zero is ALWAYS the live market ATM, regardless of any override/custom ATM.
        // Custom ATM only changes the adjustment destination, not the pricing center.
        const marketAtm = this._atmStrikeService.getCurrentAtm(this.config?.underlying);

        let atmRow: ZonesGridRow;

        if (isValidNumber(marketAtm, true)) {
            atmRow = zonesGridRows.find(r => r.strike === marketAtm);
            if (atmRow) {
                if (isVoid(atmRow.roles)) { atmRow.roles = []; }
                if (atmRow.roles.indexOf('ATM') < 0) {
                    atmRow.roles.push('ATM');
                }
            }
        }

        // Fallback: if market ATM row not in data, try role-based detection
        if (isVoid(atmRow)) {
            atmRow = zonesGridRows.find(r =>
                r.roles?.indexOf('ATM') >= 0
            );
        }

        const baseStrike = atmRow?.strike || (isValidNumber(marketAtm, true) ? marketAtm : 0);

        // Build display rows for all strikes that have data for the selected expiration
        const displayRows: PricingStepDisplayRow[] = [];
        for (const row of zonesGridRows) {
            const col = row.columns.find(c => c.expirationDatePretty === expiration);
            if (isVoid(col)) {
                continue;
            }
            const offset = row.strike - baseStrike;
            displayRows.push({
                strike: row.strike,
                column: col,
                roles: row.roles,
                offsetLabel: this.formatOffsetLabel(offset),
            });
        }

        // Filter to ATM-centered view: numSteps above + ATM + numSteps below + natural levels
        const hasPositionRole = (row: PricingStepDisplayRow): boolean =>
            row.roles?.some(r => r !== 'ATM') || false;

        const numSteps = this.config?.numSteps || 5;

        const atmRows = displayRows.filter(r => r.strike === baseStrike);

        const naturalRows = displayRows.filter(r =>
            r.strike !== baseStrike && hasPositionRole(r)
        );

        const stepRowsAbove = displayRows
            .filter(r => r.strike > baseStrike && !hasPositionRole(r))
            .sort((a, b) => a.strike - b.strike)
            .slice(0, numSteps);

        const stepRowsBelow = displayRows
            .filter(r => r.strike < baseStrike && !hasPositionRole(r))
            .sort((a, b) => b.strike - a.strike)
            .slice(0, numSteps);

        this.rows = [...naturalRows, ...stepRowsAbove, ...atmRows, ...stepRowsBelow]
            .sort((a, b) => b.strike - a.strike);
    }

    @DetectMethodChanges()
    close(): void {
        this.isVisible = false;
        this.rows = [];
        this.isLoading = false;
        this.unsubscribeStickyAtm();
        this.root?.resetPricingStepsSubscriptions();
    }

    onCloseClick(): void {
        this.close();
    }

    getStrikeStep(): number {
        return getStrikeStep(this.config?.underlying);
    }

    async applySettings(): Promise<void> {
        if (!isValidNumber(this.settings.numOfSteps)) {
            this._toastr.error('Number of steps must be a positive integer');
            return;
        }

        if (!isValidNumber(this.settings.stepSize)) {
            this._toastr.error('Step size must be a positive integer');
            return;
        }

        if (!this.isSuperUser) {
            if (this.settings.numOfSteps > 10) {
                this.settings.numOfSteps = 10;
            }
            if (this.allowedStepSizes.indexOf(this.settings.stepSize) < 0) {
                this.settings.stepSize = 10;
            }
        }

        const underlying = this.config?.underlying;
        if (underlying) {
            await this._userSettings.setValue(
                `apg-pricing-steps.${underlying}.settings`,
                this.settings,
            );
        }

        this.config.numSteps = this.settings.numOfSteps;
        this.config.stepSize = this.settings.stepSize;

        this.root?.resetPricingStepsSubscriptions();
        this.isLoading = true;
        this._changeDetector.detectChanges();
        this.root?.loadPricingStepsData(this.config);
    }

    isPriceAvailable(row: PricingStepDisplayRow): boolean {
        return !row.column.isEmpty && isValidNumber(row.column.price);
    }

    getNetPriceClass(row: PricingStepDisplayRow): string {
        if (!this.isPriceAvailable(row)) {
            return 'net-price-na';
        }
        return row.column.price < 0 ? 'ets-negative-number' : 'ets-positive-number';
    }

    getRowRoleCss(row: PricingStepDisplayRow): string[] {
        const classes = [];
        if (isVoid(row.roles)) {
            return classes;
        }

        for (const role of row.roles) {
            if (role !== 'ATM') {
                classes.push('the-leg');
            }

            switch (role) {
                case 'ATM':
                    classes.push('current-atm');
                    break;
                case 'ShortOption':
                    classes.push('short-option');
                    break;
                case 'SpreadLongLeg':
                case 'SpreadShortLeg':
                    classes.push('spread');
                    break;
                case 'SecondSpreadLongLeg':
                case 'SecondSpreadShortLeg':
                    classes.push('second-spread');
                    break;
                case 'ProtectiveOption':
                case 'SecondProtectiveOption':
                    classes.push('protective');
                    break;
            }
        }

        return classes;
    }

    isAtm(row: PricingStepDisplayRow): boolean {
        return row.roles?.indexOf('ATM') >= 0;
    }

    private prepareDataSingle(adjustments: CashFlowAdjustment[]): ZonesGridRow[] {
        return Enumerable.from(adjustments)
            .groupBy(x => x.atmStrike + (x.zonesGridStrikeDelta || 0) + (x.zonesGridCustomAtmOffset || 0))
            .select(grp => {
                const adjs = grp.toArray();
                const atmStrike = grp.key();

                const group: AdjustmentsGroupForZonesGrid = {
                    atmStrike,
                    adjustments: adjs,
                };

                return new ZonesGridRow(group);
            })
            .toArray();
    }

    private prepareDataCouples(adjustments: CashFlowAdjustment[]): ZonesGridRow[] {
        const adjustmentType = Enumerable
            .from(adjustments)
            .select(x => x.adjustmentType)
            .distinct()
            .toJoinedString('+');

        return Enumerable.from(adjustments)
            .groupBy(x => {
                let key = x.atmStrike + (x.zonesGridStrikeDelta || 0);
                if (adjustmentType.indexOf('3') >= 0) {
                    key += (x.zonesGridCustomAtmOffset || 0);
                }
                return key;
            })
            .select(group => {
                const adjs = group.toArray();
                const atmStrike = group.key();

                const ajdGroup: AdjustmentsGroupForZonesGrid = {
                    adjustments: adjs,
                    atmStrike,
                };

                return new ZonesGridRow(ajdGroup);
            })
            .toArray();
    }

    private formatOffsetLabel(offsetPoints: number): string {
        if (offsetPoints === 0) {
            return '0';
        }
        return offsetPoints > 0 ? `+${offsetPoints}` : `${offsetPoints}`;
    }

    private subscribeStickyAtm(config: ApgPricingStepsConfig): void {
        this.unsubscribeStickyAtm();

        this._atmStrikeService.watch(config.underlying, 'Sticky');

        this._stickyAtmSub = this._messageBus
            .of<AtmStrikeChangedEvent>('AtmStrikeChangedEvent:Sticky')
            .pipe(
                filter(msg => msg.payload.underlying === config.underlying),
                debounceTime(3000)
            )
            .subscribe(msg => {
                if (!this.isVisible || isVoid(this.config) || this.isLoading) {
                    return;
                }

                const confirmedAtm = this._atmStrikeService.getCurrentAtm(
                    config.underlying, 'Sticky'
                );

                if (confirmedAtm !== msg.payload.currentAtm) {
                    return;
                }

                this.root?.resetPricingStepsSubscriptions();
                this.isLoading = true;
                this._changeDetector.detectChanges();
                this.root?.loadPricingStepsData(this.config);
            });
    }

    private unsubscribeStickyAtm(): void {
        if (this._stickyAtmSub) {
            this._stickyAtmSub.unsubscribe();
            this._stickyAtmSub = undefined;
        }
    }
}
